/**
 * Перевод вне React: модели экранов, словари подписей, форматтеры чисел и
 * дат (`astra/lib/format.ts`, `lib/workVocab.ts`, `*View.ts`, `*Model.ts`).
 *
 * Таких модулей сотни, и протаскивать в каждый `intl` параметром — значит
 * переписать сигнатуры всего приложения ради языка. Поэтому провайдер
 * (`I18nProvider`) публикует свой `intl` сюда, а модуль зовёт `tr(...)` в
 * момент построения строки.
 *
 * Правила, без которых это ломается:
 *
 * * `tr` зовётся ВО ВРЕМЯ РЕНДЕРА (или из функции, которую зовёт рендер), а
 *   не при импорте модуля: константа `const LABEL = tr(m.x)` навсегда
 *   застынет на языке первой загрузки. Словари хранят описания сообщений
 *   (`defineMessages`), а строку делает функция-подпись при вызове;
 * * смена языка перемонтирует дерево приложения (`I18nProvider`, ключ по
 *   языку): так строки, собранные в `useMemo` по данным, тоже пересобираются.
 *
 * Без провайдера (тесты моделей, изолированные кадры) — язык-источник:
 * `defaultMessage` описания, то есть прежний русский текст.
 */
import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';
import type { Message, RichTag } from './define';
import type { MessageId } from './messageIds.generated';
import { fallbackIntl } from './context';
import { SOURCE_LOCALE, type Locale, isLocale } from './locales';

let current: IntlShape = fallbackIntl;

/** Провайдер публикует `intl` показанного языка. */
export function setRuntimeIntl(intl: IntlShape): void {
  current = intl;
}

/** `intl` показанного языка (числа, даты, `formatMessage`). */
export function runtimeIntl(): IntlShape {
  return current;
}

/** Показанный язык интерфейса. */
export function runtimeLocale(): Locale {
  return isLocale(current.locale) ? current.locale : SOURCE_LOCALE;
}

/**
 * Локаль `Intl` для ЧИСЕЛ. Узбекский CLDR пишет числа так же, как русский:
 * разряды — неразрывным пробелом, дробь — запятой («8 107,5»). Но не каждый
 * браузер несёт данные `uz` (урезанный ICU отдаёт английское «8,107.5»), поэтому
 * числа всех трёх языков форматируются шаблоном `ru-RU` — результат совпадает
 * с узбекским CLDR и не зависит от сборки браузера (журнал №1114).
 */
export function numberLocale(): string {
  return 'ru-RU';
}

/** Число по шаблону языка интерфейса (`numberLocale`): «8 107,5». */
export function formatNum(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(numberLocale(), options).format(value);
}

/** Язык интерфейса — язык-источник (русский)? */
export function isSourceLocale(): boolean {
  return runtimeLocale() === SOURCE_LOCALE;
}

/** Аргументы сообщения по его id (выведены из ICU источника, `messageIds.generated.ts`). */
export type MessageValues<K extends MessageId> = FormatjsIntl.MessageArguments[K];

/**
 * Аргументы вызова: сообщение без аргументов — без них (или произвольный
 * объект), с аргументами — ровно его объект. Проверка НЕ распределяется по
 * союзу id: сообщение из словаря (`Message` с союзом id) считается «без
 * аргументов», а не 9 тысяч перегрузок `formatMessage` react-intl (на них
 * `tsc` шёл десятки минут — журнал №1113).
 */
export type ValuesArg<K extends MessageId> = [keyof MessageValues<K>] extends [never]
  ? [values?: Record<string, unknown>]
  : [values: MessageValues<K>];

/** Есть ли в сообщении теги разметки (`<b>…</b>`): тогда результат — React-узлы. */
type HasTags<K extends MessageId> = [Extract<MessageValues<K>[keyof MessageValues<K>], RichTag>] extends [never]
  ? false
  : true;

/** Результат `t()`: строка, а у сообщения с тегами — React-узлы. */
export type TResult<K extends MessageId> = HasTags<K> extends true ? ReactNode : string;

/** Сигнатура `t` из `useT()`. */
export type TFunction = <K extends MessageId>(message: Message<K>, ...values: ValuesArg<K>) => TResult<K>;

/** Форматировать сообщение данным `intl`: строка или, при тегах, массив узлов. */
export function formatWith(intl: IntlShape, message: Message, values?: object): ReactNode {
  const out = intl.formatMessage(message as never, values as never) as unknown;
  if (Array.isArray(out)) return out.every((part) => typeof part === 'string') ? out.join('') : (out as ReactNode);
  return out as ReactNode;
}

/**
 * Строка показанного языка — для модулей без хуков:
 * `tr(messages.title)`, `tr(messages.count, { count })`. Сообщение с
 * аргументами без них не пройдёт `tsc`. Для разметки внутри строки
 * (`<b>…</b>`) — `useT()` в компоненте: `tr` возвращает только текст.
 */
export function tr<K extends MessageId>(message: Message<K>, ...[values]: ValuesArg<K>): string {
  const out = formatWith(current, message as Message, values ?? undefined);
  return Array.isArray(out) ? out.join('') : String(out);
}
